import * as React from 'react';
import UrlParams from '@mam/common-components/src/UrlParams';

export enum TabStateName {
	info = 'info',
	availability = 'availability',
	irp = 'irp',
	timeToPrice = 'timeToPrice',
	prices = 'prices',
	reimbursement = 'reimbursement',
	registration = 'registration',
	history = 'history',
}

export type Props = { virtualBranId: string | null; virtualPackId: string | null; tabName: TabStateName | null };

export const DEFAULT_TABNAME: TabStateName.info = TabStateName.info;

const VIRTUAL_BRANDS_URL_KEY = 'analyticsVirtualBrands';

type MaybeUrlTabState = {
	tabName: string | null;
	virtualPackId: string | null;
};

const normalizeTabState = (
	maybeTabState: MaybeUrlTabState
): { tabName: TabStateName; virtualPackId: string | null } => {
	if (
		!Object.entries(TabStateName).some(
			([_, value]) => value.toLowerCase() === (maybeTabState.tabName || '').toLowerCase()
		)
	) {
		return {
			...maybeTabState,
			tabName: TabStateName.info,
		};
	}

	return maybeTabState as { tabName: TabStateName; virtualPackId: string | null };
};

export type BrandDetailUrlParams = { [virtualBrandId: string]: Props };

export const parseBrandDetailUrlParams = (query: string): BrandDetailUrlParams => {
	const parsedQuery = new URLSearchParams(query);

	try {
		const data = JSON.parse(parsedQuery.get(VIRTUAL_BRANDS_URL_KEY) || '{}');
		return (Object.entries(data) as [string, MaybeUrlTabState][])
			.map(([virtualBrandId, state]): [string, MaybeUrlTabState] => [virtualBrandId, normalizeTabState(state)])
			.reduce((aggregator, [virtualBrandId, state]) => ({ ...aggregator, [virtualBrandId]: state }), {});
	} catch (_) {
		return {};
	}
};

const BrandDetailUrlState = ({ virtualBranId, virtualPackId, tabName }: Props) => {
	const query = new URLSearchParams(window.location.search);
	const prevVirtualBrands = JSON.parse(query.get(VIRTUAL_BRANDS_URL_KEY) || '{}');

	if (virtualBranId) {
		const nextState: {
			[virtualBrandId: string]: MaybeUrlTabState;
		} = {
			...prevVirtualBrands,
			[virtualBranId]: {
				...(virtualPackId ? { virtualPackId } : {}),
				...(tabName && tabName !== DEFAULT_TABNAME ? { tabName } : {}),
			},
		};

		const asEntries: [string, MaybeUrlTabState][] = Object.entries(nextState);
		if (asEntries.length > 0 && asEntries.every(([_, state]) => Object.keys(state).length > 0)) {
			query.set(VIRTUAL_BRANDS_URL_KEY, JSON.stringify(nextState));
		} else {
			query.delete(VIRTUAL_BRANDS_URL_KEY);
		}
	}

	return <UrlParams query={query.toString()} />;
};

export default React.memo(BrandDetailUrlState);
